import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { CButton } from '@/components/CButton';
import { CenteredSpinner } from '@/components/CenteredState';
import { isPreloadReloadPending, preloadReloadPendingMs } from '@/core/infrastructure/preload';

interface ErrorBoundaryProps {
  children: ReactNode;
  resetKeys?: string[];
  t: TFunction<'common'>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundaryClass extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
  };

  private pendingReloadTimer?: number;

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetKeys } = this.props;
    const { hasError } = this.state;

    if (!hasError || !resetKeys) {
      return;
    }

    const prevResetKeys = prevProps.resetKeys;

    if (prevResetKeys === resetKeys) {
      return;
    }

    if (!prevResetKeys || resetKeys.length !== prevResetKeys.length) {
      this.resetState();
      return;
    }

    for (let i = 0; i < resetKeys.length; i++) {
      if (resetKeys[i] !== prevResetKeys[i]) {
        this.resetState();
        return;
      }
    }
  }

  private resetState = () => {
    window.clearTimeout(this.pendingReloadTimer);
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
    });
  };

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });

    if (isPreloadReloadPending()) {
      window.clearTimeout(this.pendingReloadTimer);
      this.pendingReloadTimer = window.setTimeout(() => this.forceUpdate(), preloadReloadPendingMs);
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.pendingReloadTimer);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { t } = this.props;

    if (hasError && isPreloadReloadPending()) {
      return <CenteredSpinner />;
    }

    if (hasError) {
      return (
        <div className="c-error-boundary">
          <div className="c-error-boundary__card">
            <h2 className="c-error-boundary__title">{t('common.error_boundary.title')}</h2>
            <p className="c-error-boundary__text">{t('common.error_boundary.description')}</p>
            <CButton variant="primary" onClick={this.handleReload}>
              {t('common.error_boundary.reload')}
            </CButton>
            {import.meta.env.DEV && (
              <details className="c-error-boundary__details">
                <summary className="cursor-pointer">{t('common.error_boundary.details')}</summary>
                <div className="mt-2 max-h-[200px] overflow-y-auto p-3">
                  <pre className="whitespace-pre-wrap">{error?.toString()}</pre>
                  <pre className="mt-2 whitespace-pre-wrap">{errorInfo?.componentStack}</pre>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { t } = useTranslation('common');

  return (
    <ErrorBoundaryClass resetKeys={[location.pathname, location.search]} t={t}>
      {children}
    </ErrorBoundaryClass>
  );
}
